import { captureMarketingCampaign, clearMarketingCampaign, readMarketingCampaign } from "./marketing-attribution";
import { marketingEventSchema, type MarketingEvent } from "../../entities/marketing/marketing-event";
import { apiClient } from "../../shared/api/client";
import { updateStorefrontTrackingConsent, marketingRequestOptions, pixelIdSchema, type MarketingContext, type TrackingSettings } from "./marketing-api";

export type MarketingConsent = "accepted" | "declined" | null;
export interface BrowserMarketingEvent {
  readonly event: "PageView" | "InitiateCheckout" | "PaymentGenerated" | "Purchase";
  readonly event_id: string;
  readonly params: Readonly<Record<string, string | number | readonly string[]>>;
}
const consentKey = "alfabetizando:marketing-consent";
const consentDecisionKey = consentKey + ":decision";
const consentTimestampKey = consentKey + ":updated-at";
const consentLifetimeMs = 180 * 24 * 60 * 60 * 1000;
const eventKey = "alfabetizando:marketing-events";
const checkoutKey = "alfabetizando:marketing-checkout";
const listeners = new Set<() => void>();
const checkouts = new Set<string>();
const sent = new Set<string>();
let consent: MarketingConsent = null;
let loadedConsent = false;
let consentUpdatedAt = 0;
let decisionGeneration = 0;
let consentRequests = Promise.resolve(true);
let revision = 0;
let storageRevision = 0;
let expiryTimer: number | undefined;
let revokeScripts: (() => void) | null = null;
let settings: TrackingSettings = { enabled: false, meta_pixel_id: null, gtm_container_id: null };
let settingsLoaded = false;
let transport: ((event: BrowserMarketingEvent) => void) | null = null;
const synchronizations = new Map<string, Promise<boolean>>();

export function publicTrackingPath(input: string): string | null {
  const path = input.split(/[?#]/u, 1)[0] ?? "";
  if (["/", "/index.html", "/catalogo", "/busca", "/clube", "/checkout", "/carrinho"].includes(path)) return path === "/index.html" ? "/" : path;
  if (/^\/(produto|categoria)\/[a-z0-9-]+$/u.test(path)) return path.startsWith("/produto/") ? "/produto" : "/categoria";
  if (/^\/pedido\/[^/]+\/confirmacao$/u.test(path)) return "/pedido/confirmacao";
  return null;
}
export function safeBrowserTrackingUrl(input: string): boolean {
  try {
    const url = new URL(input, window.location.origin);
    const safeSource = (url.pathname === "/checkout" || /^\/produto\/[a-z0-9-]+$/u.test(url.pathname)) &&
      [...url.searchParams].length === 1 && url.searchParams.get("source") === "member_area";
    return url.origin === window.location.origin && (!url.search || safeSource) && !url.hash &&
      publicTrackingPath(url.pathname) !== null && !url.pathname.startsWith("/pedido/");
  } catch { return false; }
}
export function canTrackInBrowser(): boolean {
  if (!safeBrowserTrackingUrl(window.location.href)) return false;
  if (!document.referrer || safeBrowserTrackingUrl(document.referrer)) return true;
  try {
    const referrer = new URL(document.referrer);
    return referrer.origin !== window.location.origin && referrer.pathname === "/" && !referrer.search && !referrer.hash;
  } catch { return false; }
}
export function getMarketingConsent(): MarketingConsent {
  if (!loadedConsent) {
    loadedConsent = true;
    try {
      const saved = localStorage.getItem(consentKey);
      consent = saved === "accepted" || saved === "declined" ? saved : null;
      const timestamp = localStorage.getItem(consentTimestampKey);
      consentUpdatedAt = timestamp === null ? Date.now() : Number(timestamp);
      if (consent && timestamp === null) localStorage.setItem(consentTimestampKey, String(consentUpdatedAt));
    } catch { consent = null; }
  }
  if (consent && (consent === "accepted" && marketingLocks() === null || !Number.isFinite(consentUpdatedAt) || consentUpdatedAt <= 0 ||
    Date.now() - consentUpdatedAt >= consentLifetimeMs || consentUpdatedAt > Date.now() + 60_000)) {
    consent = null; withdrawLegacyTracking();
    decisionGeneration += 1;
    storageRevision += 1;
    scheduleConsentExpiry();
    queueMicrotask(notify);
    try { localStorage.removeItem(consentKey); localStorage.removeItem(consentTimestampKey); } catch { /* Expired consent remains inactive in memory. */ }
  }
  return consent;
}
function receiveStorageConsent(event: StorageEvent): void {
  if (event.key !== consentKey && event.key !== consentTimestampKey && event.key !== consentDecisionKey) return;
  decisionGeneration += 1;
  loadedConsent = false;
  if (getMarketingConsent() !== "accepted") withdrawLegacyTracking();
  storageRevision += 1;
  scheduleConsentExpiry();
  notify();
}
export function subscribeMarketing(listener: () => void): () => void {
  if (listeners.size === 0) window.addEventListener("storage", receiveStorageConsent);
  listeners.add(listener);
  scheduleConsentExpiry();
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) window.removeEventListener("storage", receiveStorageConsent);
    scheduleConsentExpiry();
  };
}
export function getMarketingStorageRevision(): number { return storageRevision; }
export function setMarketingRevoker(revoke: () => void): void { revokeScripts = revoke; }
export function getMarketingRevision(): number { return revision; }
function notify() { revision += 1; listeners.forEach((listener) => { listener(); }); }
function scheduleConsentExpiry(): void {
  if (expiryTimer !== undefined) window.clearTimeout(expiryTimer);
  expiryTimer = undefined;
  if (!consent || listeners.size === 0) return;
  const remaining = Math.max(1, consentUpdatedAt + consentLifetimeMs - Date.now());
  expiryTimer = window.setTimeout(() => {
    expiryTimer = undefined;
    getMarketingConsent();
    scheduleConsentExpiry();
  }, Math.min(remaining, 2_147_483_647));
}
export function setMarketingSettings(value: TrackingSettings): void {
  settings = value; settingsLoaded = true;
  if (!value.enabled && !value.utmify_enabled) clearMarketingCampaign();
  notify();
}
export function getMarketingSettings(): TrackingSettings { return settings; }
export function automaticPixelEnabled(): boolean {
  return settings.enabled && pixelIdSchema.safeParse(settings.meta_pixel_id).success;
}
function withdrawLegacyTracking(): void {
  if (!automaticPixelEnabled()) {
    transport = null;
    if (!settings.utmify_enabled || consent === "declined") clearMarketingCampaign();
  }
  revokeScripts?.();
}
export function setMarketingTransport(value: ((event: BrowserMarketingEvent) => void) | null): void {
  transport = value;
}
export function applyMarketingConsent(value: MarketingConsent): void {
  loadedConsent = true; consent = value; consentUpdatedAt = Date.now();
  if (value !== "accepted") withdrawLegacyTracking();
  try {
    if (value === null) { localStorage.removeItem(consentKey); localStorage.removeItem(consentTimestampKey); }
    else { localStorage.setItem(consentKey, value); localStorage.setItem(consentTimestampKey, String(consentUpdatedAt)); }
  } catch { /* Preferences remain in memory when storage is unavailable. */ }
  scheduleConsentExpiry();
  notify();
}
function marketingLocks(): LockManager | null {
  try {
    const locks = (navigator as { readonly locks?: LockManager }).locks;
    return typeof locks?.request === "function" ? locks : null;
  } catch { return null; }
}
function writeConsentDecision(): string | null {
  try {
    const id = crypto.randomUUID();
    localStorage.setItem(consentDecisionKey, id);
    return localStorage.getItem(consentDecisionKey) === id ? id : null;
  } catch { return null; }
}
function currentConsentDecision(id: string | null): boolean {
  if (id === null) return false;
  try { return localStorage.getItem(consentDecisionKey) === id; } catch { return false; }
}
async function requestExplicitConsent(value: Exclude<MarketingConsent, null>, generation: number, decision: string | null): Promise<boolean> {
  const grant = value === "accepted";
  const locks = marketingLocks();
  if (grant && (!locks || decision === null)) return false;
  const send = () => {
    if ((grant || decision !== null) && (generation !== decisionGeneration || !currentConsentDecision(decision))) return Promise.resolve(false);
    return updateStorefrontTrackingConsent(grant);
  };
  try {
    return locks ? await locks.request("alfabetizando:marketing-consent", send) : await send();
  } catch { return false; }
}
export function setMarketingConsent(value: MarketingConsent): Promise<boolean> {
  const generation = ++decisionGeneration;
  if (value === null) { applyMarketingConsent(null); return Promise.resolve(true); }
  const decision = writeConsentDecision();
  applyMarketingConsent(value === "declined" ? "declined" : null);
  const request = consentRequests.then(() => requestExplicitConsent(value, generation, decision));
  consentRequests = request;
  return request.then((acknowledged) => {
    if (generation !== decisionGeneration || !acknowledged || (decision !== null && !currentConsentDecision(decision))) return false;
    if (value === "accepted") applyMarketingConsent("accepted");
    const recent = recentMarketingCheckout();
    const targets = new Set(checkouts);
    if (recent) targets.add(recent);
    if (value !== "accepted" || !window.location.pathname.startsWith("/pedido/")) {
      for (const id of targets) void syncCheckoutTracking(id);
    }
    return true;
  });
}
function marketingCookie(name: string): string | null {
  const value = document.cookie.split("; ").find((part) => part.startsWith(name + "="))?.slice(name.length + 1);
  return value && value.length <= 256 && /^fb\.\d\.\d{10,16}\.[A-Za-z0-9_-]{1,200}$/u.test(value) ? value : null;
}
export function captureMarketingAttribution(input = window.location.href): void {
  let url: URL;
  try { url = new URL(input, window.location.origin); } catch { return; }
  const path = publicTrackingPath(url.pathname);
  const choice = getMarketingConsent();
  const automaticUtmify = settings.utmify_enabled === true && choice !== "declined";
  const legacyConsent = Boolean(settings.enabled || settings.utmify_enabled) && choice === "accepted";
  if (!automaticPixelEnabled() && !automaticUtmify && !legacyConsent) {
    if (settingsLoaded) clearMarketingCampaign();
    return;
  }
  if (path !== null && !url.pathname.startsWith("/pedido/")) captureMarketingCampaign(url.href);
}
export function getCheckoutMarketingData(): { marketing_consent: boolean; marketing_context: MarketingContext | null } {
  const path = publicTrackingPath(window.location.pathname);
  const publicPage = path !== null && !window.location.pathname.startsWith("/pedido/");
  const legacyConsent = Boolean(settings.enabled || settings.utmify_enabled) && getMarketingConsent() === "accepted" && publicPage;
  const automaticUtmify = settings.utmify_enabled === true && getMarketingConsent() !== "declined";
  const allowed = publicPage && (automaticPixelEnabled() || automaticUtmify || legacyConsent);
  captureMarketingAttribution();
  let fbc = allowed && settings.enabled ? marketingCookie("_fbc") : null;
  const clickId = allowed && settings.enabled ? new URL(window.location.href).searchParams.get("fbclid") : null;
  if (!fbc && clickId && /^[A-Za-z0-9_-]{1,200}$/u.test(clickId)) {
    fbc = "fb.1." + String(Date.now()) + "." + clickId;
    document.cookie = "_fbc=" + fbc + "; Path=/; SameSite=Lax" + (window.location.protocol === "https:" ? "; Secure" : "");
  }
  return { marketing_consent: legacyConsent, marketing_context: allowed ? { source_path: path, fbp: settings.enabled ? marketingCookie("_fbp") : null, fbc, ...readMarketingCampaign() } : null };
}
function recentMarketingCheckout(): string | null {
  try {
    const stored: unknown = JSON.parse(sessionStorage.getItem(checkoutKey) ?? "null");
    if (typeof stored === "object" && stored !== null && "id" in stored && "expires_at" in stored &&
      typeof stored.id === "string" && /^[A-Za-z0-9_-]{1,128}$/u.test(stored.id) &&
      typeof stored.expires_at === "string" && Date.parse(stored.expires_at) > Date.now()) return stored.id;
  } catch { /* An unavailable tab store does not affect checkout. */ }
  return null;
}
export function registerMarketingCheckout(id: string, expiresAt: string): () => void {
  checkouts.add(id);
  try { sessionStorage.setItem(checkoutKey, JSON.stringify({ id, expires_at: expiresAt })); } catch { /* In-memory recovery still works. */ }
  return () => { checkouts.delete(id); };
}
export function syncCheckoutTracking(checkoutId: string): Promise<boolean> {
  const previous = synchronizations.get(checkoutId) ?? Promise.resolve(true);
  const next = previous.then(async () => {
    const current = getCheckoutMarketingData();
    const controller = new AbortController();
    const timeout = window.setTimeout(() => { controller.abort(); }, 5_000);
    try {
      const { data, response } = await apiClient.POST("/checkout/sessions/{checkout_id}/tracking", {
        ...marketingRequestOptions(controller.signal), keepalive: true, params: { path: { checkout_id: checkoutId } },
        body: { consent: current.marketing_consent, context: current.marketing_context },
      });
      return response.ok && data?.accepted === true;
    } catch { return false; } finally { window.clearTimeout(timeout); }
  });
  synchronizations.set(checkoutId, next);
  return next;
}
export function alreadySent(id: string): boolean {
  try {
    const stored: unknown = JSON.parse(sessionStorage.getItem(eventKey) ?? "[]");
    if (Array.isArray(stored) && stored.every((item) => typeof item === "string")) {
      for (const item of stored.slice(-200)) sent.add(item);
    }
  } catch { /* In-memory deduplication remains available. */ }
  return sent.has(id);
}
export function rememberMarketingEvent(id: string): void {
  sent.add(id);
  try { sessionStorage.setItem(eventKey, JSON.stringify([...sent].slice(-200))); } catch { /* No dependency on storage. */ }
}
export function emitBrowserMarketingEvent(event: BrowserMarketingEvent): void {
  if (!settings.enabled || (!automaticPixelEnabled() && getMarketingConsent() !== "accepted") || !canTrackInBrowser() || !transport || alreadySent(event.event_id)) return;
  try {
    transport(event); rememberMarketingEvent(event.event_id);
  } catch { /* Marketing cannot interrupt a purchase. */ }
}
export function emitMarketingEvents(events: readonly MarketingEvent[]): void {
  for (const input of events) {
    const parsed = marketingEventSchema.safeParse(input);
    if (!parsed.success) continue;
    const event = parsed.data;
    emitBrowserMarketingEvent({
      event: event.event_name, event_id: event.event_id,
      params: { value: event.value_cents / 100, currency: event.currency,
        content_ids: event.content_ids, content_type: "product", payment_method: event.payment_method,
        page_path: publicTrackingPath(window.location.pathname) ?? "/checkout" },
    });
  }
}
